import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useReactFlow } from 'reactflow';
import { NODE_THEMES } from '../Components/NodeTheme';
import { EditorDockContext, EditorNodeIdContext, StartNodeContext } from '../Components/NodeEditorChrome';
import { canBeStartNode } from '../Components/startNode';
import ViewBasicAnswer from './Nodes/BasicAnswer';
import ViewTransferAgent from './Nodes/TransferAgent';
import ViewTags from './Nodes/Tags';
import ViewSaveAnswer from './Nodes/SaveAnswer';
import ViewAssistant from './Nodes/Assistant';
import ViewFlowsWhatsApp from './Nodes/FlowsWhatsApp';
import ViewQueryApi from './Nodes/QueryApi';
import ViewConditions from './Nodes/Conditions';
import ViewJson from './Nodes/Json';
import ViewWebHook from './Nodes/WebHook';
import ViewTemplate from './Nodes/Template';

const STORAGE_KEY = 'su.nodeEditorLayout';
const MIN_WIDTH = 340;
const MAX_WIDTH = 720;

const readLayout = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    return { floating: Boolean(saved.floating), width: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Number(saved.width) || 400)) };
  } catch (error) {
    return { floating: false, width: 400 };
  }
};

const NodeEditorPanel = ({
  selectedNode,
  setSelectedNode,
  setNodes,
  removeNode,
  nodes,
  edges,
  editFlow,
  updateBot,
  truncateText,
  customStyles,
  customStylesDark,
  isDark,
  watchSelectedNode,
  setLoading,
  valueSelectedVariable,
  typeInputValue,
  typeInput,
  resetVariable,
  contextVariables,
  contextResponseApi,
  handleMouseDown,
  isDragging,
  isNodeExpanded,
  setIsNodeExpanded,
  position,
  setPosition,
  selectedLog,
  listAreas,
  listAdvisers,
  listTags,
  listAssistants,
  correspondence,
  flows,
  setConnectionTestData,
  setConnectionTestDataParams,
  setModalConnectionTest,
  setShowConditionsModal,
  setAnchorElConditionsModel,
  makeStartNode,
}) => {
  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;
  const selectedNodeId = selectedNode?.id;
  const isStart = Boolean(selectedNode?.data?.first);
  const canBeStart = canBeStartNode(selectedNode);
  const { getNode, getZoom, setCenter } = useReactFlow();
  const [layout, setLayout] = useState(readLayout);
  const [canvasTop, setCanvasTop] = useState(0);
  const [shellTop, setShellTop] = useState(0);
  const [portalTarget, setPortalTarget] = useState(null);
  const [resizing, setResizing] = useState(false);
  const cardRef = useRef(null);
  const panelWidth = isNodeExpanded ? MAX_WIDTH : layout.width;
  const docked = !layout.floating;
  const open = Boolean(selectedNode) && !selectedLog;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
    } catch (error) {
      console.log('No se pudo guardar el diseño del editor:', error);
    }
  }, [layout]);

  useLayoutEffect(() => {
    const wrapper = document.querySelector('.reactflow-wrapper');
    if (!wrapper) return undefined;
    const shell = wrapper.closest('.su-canvas-root');
    setPortalTarget(shell);
    const measure = () => {
      setCanvasTop(wrapper.getBoundingClientRect().top);
      if (shell) setShellTop(shell.getBoundingClientRect().top);
    };
    measure();
    window.addEventListener('resize', measure);
    wrapper.style.marginRight = open && docked ? `${panelWidth}px` : '0px';
    return () => {
      window.removeEventListener('resize', measure);
      wrapper.style.marginRight = '0px';
    };
  }, [open, docked, panelWidth]);

  useEffect(() => {
    if (!open || !docked || !selectedNodeId) return undefined;
    const timer = setTimeout(() => {
      const wrapper = document.querySelector('.reactflow-wrapper');
      const element = wrapper?.querySelector(`.react-flow__node[data-id="${selectedNodeId}"]`);
      const node = getNode(selectedNodeId);
      if (!wrapper || !element || !node) return;
      const area = wrapper.getBoundingClientRect();
      const box = element.getBoundingClientRect();
      if (box.right > area.right - 16 || box.left < area.left + 16) {
        setCenter(node.position.x + (node.width || 220) / 2, node.position.y + (node.height || 90) / 2, { zoom: getZoom(), duration: 250 });
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [open, docked, selectedNodeId, getNode, getZoom, setCenter]);

  const startResize = (event) => {
    event.preventDefault();
    const card = cardRef.current;
    const startX = event.clientX;
    const startWidth = card.getBoundingClientRect().width;
    let nextWidth = startWidth;
    setResizing(true);
    const move = (moveEvent) => {
      const delta = docked ? startX - moveEvent.clientX : moveEvent.clientX - startX;
      nextWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
      card.style.width = `${nextWidth}px`;
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      setResizing(false);
      setIsNodeExpanded(false);
      setLayout((current) => ({ ...current, width: Math.round(nextWidth) }));
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const startDrag = (event) => {
    if (event.target.closest('button')) return;
    event.preventDefault();
    const card = cardRef.current;
    const box = card.getBoundingClientRect();
    const offsetX = event.clientX - box.left;
    const offsetY = event.clientY - box.top;
    let nextX = box.left;
    let nextY = box.top;
    card.style.willChange = 'transform';
    const move = (moveEvent) => {
      nextX = Math.max(0, Math.min(window.innerWidth - box.width, moveEvent.clientX - offsetX));
      nextY = Math.max(canvasTop + 8, Math.min(window.innerHeight - 120, moveEvent.clientY - offsetY));
      card.style.transform = `translate3d(${nextX - box.left}px, ${nextY - box.top}px, 0)`;
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      card.style.transform = '';
      card.style.willChange = '';
      setPosition({ x: nextX, y: nextY });
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  const dockValue = useMemo(
    () => ({
      floating: layout.floating,
      toggle: () => {
        if (!layout.floating) setPosition({ x: Math.max(20, window.innerWidth - panelWidth - 24), y: canvasTop + 16 });
        setLayout((current) => ({ ...current, floating: !current.floating }));
      },
    }),
    [layout.floating, panelWidth, canvasTop, setPosition]
  );
  const dragHandle = docked ? undefined : startDrag;
  const floatTop = Math.max(canvasTop + 8, position.y);

  const startNodeValue = useMemo(
    () => ({
      isStart,
      canBeStart,
      onMakeStart: () => makeStartNode?.(selectedNodeId),
    }),
    [isStart, canBeStart, selectedNodeId, makeStartNode]
  );

  return (
    <div
      style={{
        position: 'relative',
        display: !selectedNode ? 'none' : '',
      }}
    >
      {portalTarget && createPortal(
      <div
        ref={cardRef}
        className={docked ? 'su-ed-dock' : undefined}
        style={{
          width: `${panelWidth}px`,
          maxWidth: 'calc(100vw - 80px)',
          zIndex: 960,
          display: open ? 'flex' : 'none',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: theme.panelBg,
          position: 'fixed',
          margin: 0,
          minWidth: 0,
          transition: 'none',
          ...(docked
            ? { right: 0, top: `${shellTop}px`, bottom: 0, border: 'none', borderLeft: `1px solid ${theme.border}`, borderRadius: 0, boxShadow: 'none' }
            : { left: position.x, top: `${floatTop}px`, height: `min(680px, calc(100vh - ${floatTop + 16}px))`, border: `1px solid ${theme.border}`, borderRadius: '14px', boxShadow: '0 12px 32px rgba(0, 0, 0, 0.28)' }),
        }}
      >
        <style>{`.su-ed-dock [style*="cursor: grab"] { cursor: default !important; }`}</style>
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label="Cambiar el ancho del editor"
          onPointerDown={startResize}
          style={{ position: 'absolute', top: 0, bottom: 0, [docked ? 'left' : 'right']: 0, width: '6px', zIndex: 5, cursor: 'ew-resize', backgroundColor: resizing ? '#5DCCED' : 'transparent' }}
        />
        <EditorDockContext.Provider value={dockValue}>
        <EditorNodeIdContext.Provider value={selectedNode?.id ?? null}>
        <StartNodeContext.Provider value={startNodeValue}>
        {/* Basic Anwser */}
        {!selectedLog && selectedNode?.type == 'basicAnswer' && (
          <ViewBasicAnswer
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            handleNodes={setNodes}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            watchSelectedNode={watchSelectedNode}
            handleLoading={setLoading}
            valueSelectedVariable={valueSelectedVariable}
            typeInputValue={typeInputValue}
            typeInput={typeInput}
            resetVariable={resetVariable}
            contextVariables={contextVariables}
            onMouseDown={dragHandle}
            isDragging={isDragging}
            isExpanded={isNodeExpanded}
            setIsExpanded={setIsNodeExpanded}
          />
        )}
        {/* Transfer Agent */}
        {!selectedLog &&
          selectedNode?.type == 'transferAgent' && (
            <ViewTransferAgent
              selectedNode={selectedNode}
              handleSelectedNode={setSelectedNode}
              removeNode={removeNode}
              nodes={nodes}
              edges={edges}
              editFlow={editFlow}
              updateBot={updateBot}
              truncateText={truncateText}
              customStyles={customStyles}
              customStylesDark={customStylesDark}
              isDark={isDark}
              listAreas={listAreas}
              listAdvisers={listAdvisers}
              watchSelectedNode={watchSelectedNode}
              valueSelectedVariable={valueSelectedVariable}
              typeInputValue={typeInputValue}
              typeInput={typeInput}
              resetVariable={resetVariable}
              contextVariables={contextVariables}
              onMouseDown={dragHandle}
              isDragging={isDragging}
              isExpanded={isNodeExpanded}
              setIsExpanded={setIsNodeExpanded}
            />
          )}
        {/* Tags */}
        {!selectedLog && selectedNode?.type == 'tags' && (
          <ViewTags
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            listTags={listTags}
            watchSelectedNode={watchSelectedNode}
            onMouseDown={dragHandle}
            isDragging={isDragging}
            isExpanded={isNodeExpanded}
            setIsExpanded={setIsNodeExpanded}
          />
        )}
        {/* Assistant AI */}
        {!selectedLog && selectedNode?.type == 'assistantAI' && (
          <ViewAssistant
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            listAssistants={listAssistants}
            watchSelectedNode={watchSelectedNode}
            valueSelectedVariable={valueSelectedVariable}
            typeInputValue={typeInputValue}
            typeInput={typeInput}
            resetVariable={resetVariable}
            contextVariables={contextVariables}
            onMouseDown={dragHandle}
            isDragging={isDragging}
            isExpanded={isNodeExpanded}
            setIsExpanded={setIsNodeExpanded}
          />
        )}
        {/* SaveAnswer */}
        {!selectedLog && selectedNode?.type == 'saveAnswer' && (
          <ViewSaveAnswer
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            correspondence={correspondence}
            watchSelectedNode={watchSelectedNode}
            onMouseDown={dragHandle}
            isDragging={isDragging}
            isExpanded={isNodeExpanded}
            setIsExpanded={setIsNodeExpanded}
          />
        )}
        {/* FlowsWhatsApp */}
        {!selectedLog &&
          selectedNode?.type == 'flowsWhatsApp' && (
            <ViewFlowsWhatsApp
              selectedNode={selectedNode}
              handleSelectedNode={setSelectedNode}
              removeNode={removeNode}
              nodes={nodes}
              edges={edges}
              editFlow={editFlow}
              updateBot={updateBot}
              truncateText={truncateText}
              customStyles={customStyles}
              customStylesDark={customStylesDark}
              isDark={isDark}
              flows={flows}
              watchSelectedNode={watchSelectedNode}
              onMouseDown={dragHandle}
              isDragging={isDragging}
              isExpanded={isNodeExpanded}
              setIsExpanded={setIsNodeExpanded}
            />
          )}
        {/* QueryApi */}
        {!selectedLog && selectedNode?.type == 'queryApi' && (
          <ViewQueryApi
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            handleConnectionTestData={setConnectionTestData}
            handleConnectionTestDataParams={
              setConnectionTestDataParams
            }
            handleModalConnectionTest={setModalConnectionTest}
            watchSelectedNode={watchSelectedNode}
            valueSelectedVariable={valueSelectedVariable}
            typeInputValue={typeInputValue}
            typeInput={typeInput}
            resetVariable={resetVariable}
            contextVariables={contextVariables}
            onMouseDown={dragHandle}
            isDragging={isDragging}
            isExpanded={isNodeExpanded}
            setIsExpanded={setIsNodeExpanded}
          />
        )}
        {/* Conditions */}
        {!selectedLog && selectedNode?.type == 'conditions' && (
          <ViewConditions
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            watchSelectedNode={watchSelectedNode}
            handleShowConditionsModal={setShowConditionsModal}
            handleAnchorEl={setAnchorElConditionsModel}
            contextVariables={contextVariables}
            onMouseDown={dragHandle}
            isDragging={isDragging}
            isExpanded={isNodeExpanded}
            setIsExpanded={setIsNodeExpanded}
          />
        )}
        {/* Json */}
        {!selectedLog && selectedNode?.type == 'json' && (
          <ViewJson
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            watchSelectedNode={watchSelectedNode}
            contextVariables={contextResponseApi}
            onMouseDown={dragHandle}
            isDragging={isDragging}
          />
        )}
        {/* WebHook */}
        {!selectedLog && selectedNode?.type == 'webHook' && (
          <ViewWebHook
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            contextVariables={contextVariables}
            onMouseDown={dragHandle}
            isDragging={isDragging}
            isExpanded={isNodeExpanded}
            setIsExpanded={setIsNodeExpanded}
          />
        )}
        {/* Template */}
        {!selectedLog && selectedNode?.type == 'template' && (
          <ViewTemplate
            selectedNode={selectedNode}
            handleSelectedNode={setSelectedNode}
            removeNode={removeNode}
            nodes={nodes}
            edges={edges}
            editFlow={editFlow}
            updateBot={updateBot}
            truncateText={truncateText}
            customStyles={customStyles}
            customStylesDark={customStylesDark}
            isDark={isDark}
            watchSelectedNode={watchSelectedNode}
            contextVariables={contextVariables}
            onMouseDown={dragHandle}
            isDragging={isDragging}
            isExpanded={isNodeExpanded}
            setIsExpanded={setIsNodeExpanded}
          />
        )}
        </StartNodeContext.Provider>
        </EditorNodeIdContext.Provider>
        </EditorDockContext.Provider>
      </div>,
      portalTarget)}
    </div>
  );
};

export default React.memo(NodeEditorPanel);
